// useEstante.js — a estante de e-books do perfil: o que a pessoa pode ler,
// baixar e comprar, decidido pelo SERVIDOR.
//
// ================= POR QUE ELE EXISTE, E NÃO `useLivrosAdquiridos` =================
//
// A estante deixou de ser "o que eu comprei". Ela passou a ser
// "o que eu comprei OU comecei a ler pelo Clube (10% ou mais)", e essa segunda
// metade não existe em `livros-usuario-editora` — aquele endpoint só conhece
// `livros_adquiridos` e o brinde do impresso. Sem esta união, o assinante que
// lê o acervo inteiro sem comprar nada tem uma estante vazia, e o Clube parece
// não deixar rastro nenhum na conta dele.
//
// `useLivrosAdquiridos` continua vivo e não pode ser trocado por este hook nas
// outras telas: lá a pergunta é "é dono?" (esconde o botão de compra), aqui é
// "o que aparece na minha estante e em que estado". São perguntas diferentes, e
// respondê-las com a mesma lista foi o que criou o problema acima.
//
// ================= A REGRA DE ACESSO NÃO MORA AQUI =================
//
// Cada livro já chega com `pode_ler`, `pode_baixar`, `mostrar_comprar` e
// `mensagem` resolvidos por GET /minha-estante — o mesmo lugar que decide a
// leitura em /leitor/{id}. A tela só desenha. Recalcular a matriz no navegador
// criaria uma SEGUNDA regra de acesso que, um dia, responde diferente da
// primeira; a divergência apareceria como livro que a estante promete e o
// leitor recusa. Checagem no navegador nunca é permissão: é só o que evita
// mostrar o botão errado.
//
// A ordem da lista também vem pronta (quem tem progresso primeiro, por
// `lido_em`). Reordenar aqui desfaria o "continue lendo" no topo.
//
// GET {API}minha-estante  (Bearer)
//   → { clube, download_habilitado, livros: [...] }
import { useCallback, useEffect, useRef, useState } from "react";

const API_URL = process.env.REACT_APP_API;

const pegarToken = () => localStorage.getItem("token");

// Nome do arquivo salvo. O título vem do catálogo e pode ter ":", "/" e afins,
// que o Windows recusa no nome do arquivo — o download falharia depois de o
// PDF inteiro já ter descido, que é o pior momento para falhar.
const nomeDoArquivo = (titulo) => {
  const limpo = String(titulo || "livro")
    .replace(/[\\/:*?"<>|]+/g, "-")
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, 80);
  return (limpo || "livro") + ".pdf";
};

// Mensagem de erro do download por status. Os três casos são diferentes para
// quem está lendo a tela, e um "erro ao baixar" genérico transformaria os dois
// primeiros em ticket de suporte:
//   403 download_desativado → é o site inteiro, não a conta dela;
//   403 sem motivo          → ela não é dona DESTE arquivo (brinde ou Clube);
//   404                     → o livro não tem PDF cadastrado, e não há o que fazer.
const mensagemDeErro = (status, corpo) => {
  if (status === 401 || (status === 404 && corpo?.error === "User not found")) {
    return "Sua sessão expirou. Entre novamente para baixar.";
  }
  if (status === 403) {
    if (corpo?.motivo === "download_desativado") {
      return corpo?.message || "O download está desativado. Os livros são lidos no próprio site.";
    }
    return "Este arquivo é de quem comprou o e-book. Você pode ler o livro no site.";
  }
  if (status === 404) return "O arquivo deste livro não está disponível no momento.";
  return "Não foi possível baixar agora. Tente novamente em instantes.";
};

const useEstante = () => {
  const [livros, setLivros] = useState([]);
  // As etiquetas que a pessoa criou, com a contagem de livros de cada uma.
  // Vêm SEPARADAS dos livros porque são os filtros do topo da tela — e uma
  // etiqueta continua existindo enquanto tiver pelo menos um livro, mesmo que
  // a busca esconda todos eles.
  const [etiquetas, setEtiquetas] = useState([]);
  const [etiquetaMax, setEtiquetaMax] = useState(20);
  const [clube, setClube] = useState(null);
  const [downloadHabilitado, setDownloadHabilitado] = useState(false);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState(null);
  const [baixandoId, setBaixandoId] = useState(null);

  // Trava do clique duplo. O PDF é grande e o botão demora; sem isto, dois
  // cliques baixam o arquivo duas vezes e o navegador salva duas cópias.
  // Ref, e não o estado: dentro do callback o estado seria o da renderização
  // anterior, ou seja, sempre nulo.
  const baixandoRef = useRef(null);

  const carregar = useCallback(async () => {
    const token = pegarToken();

    // Deslogado: lista vazia e `loading` FALSO. Deixar o loading preso em true
    // prenderia a tela em "Carregando..." para sempre.
    if (!token) {
      setLivros([]);
      setEtiquetas([]);
      setClube(null);
      setDownloadHabilitado(false);
      setLoading(false);
      return;
    }

    setLoading(true);
    setErro(null);
    try {
      const resp = await fetch(`${API_URL}minha-estante`, {
        headers: { Accept: "application/json", Authorization: "Bearer " + token },
      });
      if (!resp.ok) throw new Error("Não foi possível carregar sua estante.");
      const d = await resp.json();
      setLivros(Array.isArray(d?.livros) ? d.livros : []);
      setEtiquetas(Array.isArray(d?.etiquetas) ? d.etiquetas : []);
      setEtiquetaMax(Number(d?.etiqueta_max) || 20);
      setClube(d?.clube || null);
      setDownloadHabilitado(!!d?.download_habilitado);
    } catch (e) {
      setErro(e?.message || "Não foi possível carregar sua estante.");
      setLivros([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    carregar();
  }, [carregar]);

  /**
   * Baixa o PDF de um livro da estante.
   *
   * NÃO é um `<a href>`: o arquivo mora atrás do Bearer e link nenhum carrega
   * cabeçalho. Por isso a resposta desce como blob e o salvamento é feito à
   * mão — é também o que permite mostrar erro em português quando o servidor
   * recusa, em vez de abrir uma aba com um JSON de 403.
   *
   * Lança Error com a mensagem pronta para a tela mostrar.
   */
  const baixar = useCallback(async (livro) => {
    if (!livro?.id || baixandoRef.current) return;

    const token = pegarToken();
    if (!token) throw new Error("Sua sessão expirou. Entre novamente para baixar.");

    baixandoRef.current = livro.id;
    setBaixandoId(livro.id);
    try {
      const resp = await fetch(
        `${API_URL}api/usuario-editora/get-pdf?book_id=${encodeURIComponent(livro.id)}`,
        { headers: { Authorization: "Bearer " + token } }
      );

      if (!resp.ok) {
        let corpo = null;
        try {
          corpo = await resp.json();
        } catch {
          /* recusa sem JSON: a mensagem por status já resolve */
        }
        throw new Error(mensagemDeErro(resp.status, corpo));
      }

      const blob = await resp.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = nomeDoArquivo(livro.titulo);
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      // Sem o revoke o blob fica na memória da aba até ela fechar — e são
      // dezenas de MB por livro, numa tela em que se baixa vários seguidos.
      window.URL.revokeObjectURL(url);
    } finally {
      baixandoRef.current = null;
      setBaixandoId(null);
    }
  }, []);

  /**
   * Grava o emoji e/ou a etiqueta de um livro.
   *
   * MANDA SÓ O QUE MUDOU (`{ emoji }` ou `{ etiqueta }`), porque é assim que o
   * servidor trata: campo ausente fica como está. Mandar os dois sempre faria
   * mexer no emoji apagar a etiqueta.
   *
   * A resposta traz a marca já gravada e a lista de etiquetas recalculada — a
   * tela adota os dois em vez de recarregar a estante inteira, que jogaria a
   * pessoa de volta ao topo da lista a cada emoji escolhido.
   */
  const marcar = useCallback(async (ebookId, mudancas) => {
    const token = pegarToken();
    if (!token) throw new Error("Entre na sua conta.");

    const resp = await fetch(`${API_URL}minha-estante/livro/${ebookId}/marca`, {
      method: "PUT",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
        Authorization: "Bearer " + token,
      },
      body: JSON.stringify(mudancas),
    });
    const d = await resp.json().catch(() => ({}));
    if (!resp.ok) throw new Error(d?.error || "Não foi possível salvar agora.");

    setLivros((lista) =>
      lista.map((l) =>
        Number(l.id) === Number(ebookId)
          ? { ...l, emoji: d.emoji ?? null, etiqueta_id: d.etiqueta_id ?? null }
          : l
      )
    );
    if (Array.isArray(d.etiquetas)) setEtiquetas(d.etiquetas);
    return d;
  }, []);

  return {
    livros,
    etiquetas,
    etiquetaMax,
    marcar,
    clube,
    downloadHabilitado,
    loading,
    erro,
    baixar,
    baixandoId,
    recarregar: carregar,
  };
};

export default useEstante;
